import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  ChevronDown, ChevronRight, Plus, Trash2, Edit3, Save, X,
  Camera, Loader2, ImageIcon, AlertCircle, BookOpen,
} from 'lucide-react';
import MultiDefectAuditPanel from '@/components/MultiDefectAuditPanel';

/**
 * AreaDefectsAccordion
 *
 * Phase 2 of the Appraisal Reports redesign — replaces the single
 * "ridge_condition" text field per area with an ARRAY of defect
 * entries. Each entry holds a defect_type (from a curated dropdown),
 * quantity, comment, recommendation, and an OPTIONAL photo.
 *
 * UI pattern: one accordion row per roof area (8 total). Each row
 * shows a defect count badge and, when expanded, a table of defect
 * entries + a "+ Add defect" form. Photos are lazy-loaded via
 * auth-gated GET when the accordion opens.
 */

const AREAS = [
  { key: 'ridge',     label: 'Ridge' },
  { key: 'tile',      label: 'Tile' },
  { key: 'valley',    label: 'Valley' },
  { key: 'barge',     label: 'Barge' },
  { key: 'sheet',     label: 'Sheet' },
  { key: 'spouting',  label: 'Spouting' },
  { key: 'fastening', label: 'Fastening' },
  { key: 'flashing',  label: 'Flashing' },
];

// iter298f — Map the appraisal's free-text roof_type / brand to the
// canonical roof_key the multi_defect_scripts collection uses. Order
// matters: more specific patterns first (Modern Conc before Concrete).
// Returns "" when nothing matches (no filter applied — dropdown shows
// generic entries only + everything).
function canonicaliseRoofKey(raw) {
  const s = String(raw || '').toLowerCase();
  if (!s) return '';
  if (s.includes('modern conc')) return 'modern_concrete';
  if (s.includes('old conc'))    return 'old_concrete';
  if (s.includes('colortile') || s.includes('colourtile')) return 'color_tile';
  if (s.includes('decramastic') || s.includes('tin tile')) return 'decramastic';
  if (s.includes('clay'))        return 'clay_tile';
  if (s.includes('longrun') || s.includes('long run') || s.includes('iron')) return 'longrun_iron';
  if (s.includes('concrete'))    return 'modern_concrete'; // ambiguous fallback
  return '';
}

export default function AreaDefectsAccordion({ appraisalId, roofType, roofKey }) {
  const { api } = useAuth();
  // iter298f — Multi Defect & Photo now sources dropdowns from the
  // standalone `multi_defect_scripts` collection (imported from the
  // Access DB spreadsheets). Old `defect_type_catalog` stays as a
  // legacy fallback but is no longer the primary data source.
  //
  // Data shape:
  //   scripts    = { ridge: [{id, name, comment, recommendation, roof_types}, ...], ... }
  //   defects    = { ridge: [defect entries stored on the appraisal], ... }
  const effectiveRoofKey = roofKey || canonicaliseRoofKey(roofType);
  const [scripts, setScripts] = useState({});
  const [defects, setDefects] = useState({});
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!appraisalId) return;
    setLoading(true);
    setError('');
    try {
      // Fetch multi-defect-scripts scoped to the current appraisal's
      // roof_key (empty roof_types are treated as generic — always
      // returned). Result is grouped by area for O(1) lookup below.
      const [scriptsResp, defectsResp] = await Promise.all([
        api.get(`/multi-defect-scripts${effectiveRoofKey ? `?roof_key=${encodeURIComponent(effectiveRoofKey)}` : ''}`),
        api.get(`/appraisals/${appraisalId}/defects`),
      ]);
      const grouped = {};
      for (const s of (scriptsResp.data.scripts || [])) {
        (grouped[s.area] = grouped[s.area] || []).push(s);
      }
      setScripts(grouped);
      setDefects(defectsResp.data.defects || {});
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load defects');
    } finally {
      setLoading(false);
    }
  }, [api, appraisalId, effectiveRoofKey]);

  useEffect(() => { load(); }, [load]);

  const totalDefects = Object.values(defects).reduce((s, arr) => s + (arr || []).length, 0);

  if (loading) {
    return (
      <div className="p-4 text-sm text-slate-500 flex items-center gap-2">
        <Loader2 className="animate-spin" size={14} /> Loading area defects…
      </div>
    );
  }

  return (
    <section
      data-testid="area-defects-accordion"
      className="rounded-lg border border-[#E2E2DF] bg-white overflow-hidden"
    >
      <header className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-[#E2E2DF]">
        <div>
          <h3 className="text-sm font-bold text-slate-900 font-[Manrope]">Area Defects</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Detailed inspection log — <span className="font-mono text-slate-700">{totalDefects}</span> defect{totalDefects === 1 ? '' : 's'} across {AREAS.length} areas.
          </p>
        </div>
      </header>
      {error && (
        <div className="p-3 bg-red-50 border-b border-red-100 text-xs text-red-700 flex items-center gap-2">
          <AlertCircle size={12} /> {error}
        </div>
      )}
      <div className="divide-y divide-[#E2E2DF]">
        {AREAS.map(a => (
          <AreaRow
            key={a.key}
            area={a}
            defects={defects[a.key] || []}
            scripts={scripts[a.key] || []}
            roofKey={effectiveRoofKey}
            isExpanded={!!expanded[a.key]}
            onToggle={() => setExpanded(prev => ({ ...prev, [a.key]: !prev[a.key] }))}
            onChange={() => load()}
            appraisalId={appraisalId}
            api={api}
          />
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// AreaRow — one accordion row per area
// ---------------------------------------------------------------------------
function AreaRow({ area, defects, scripts, roofKey, isExpanded, onToggle, onChange, appraisalId, api }) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // iter298g · mini audit studio — toggle a right-hand pane that lists ALL
  // scripts for this area across every roof_type with inline ↑↓ sort,
  // edit, add, delete. State is per-area (each Ridge / Tile / … keeps its
  // own toggle) so opening Ridge's audit doesn't force Tile's open too.
  const [showAudit, setShowAudit] = useState(false);

  return (
    <div data-testid={`defect-area-${area.key}`}>
      <button
        type="button"
        data-testid={`defect-area-toggle-${area.key}`}
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left transition-colors"
      >
        {isExpanded ? <ChevronDown size={14} className="text-slate-400 shrink-0" /> : <ChevronRight size={14} className="text-slate-400 shrink-0" />}
        <span className="flex-1 text-sm font-semibold text-slate-800">{area.label}</span>
        {defects.length > 0 && (
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#316536] text-white">
            {defects.length}
          </span>
        )}
        {defects.some(d => d.photo_url) && (
          <ImageIcon size={12} className="text-slate-400" title="Contains photos" />
        )}
      </button>
      {isExpanded && (
        <div className="px-4 pb-4 pt-1 bg-slate-50/40 border-t border-[#E2E2DF]">
          <div className="flex justify-end mb-2">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setShowAudit(v => !v); }}
              className={`flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-semibold rounded-md border transition-colors ${showAudit ? 'bg-purple-600 text-white border-purple-700' : 'text-purple-700 border-purple-400 hover:bg-purple-50'}`}
              data-testid={`defect-area-audit-toggle-${area.key}`}
              title="View / edit the full library of scripts for this area"
            >
              <BookOpen size={11} /> {showAudit ? 'Hide' : 'View all'} {area.label.toLowerCase()} defects
            </button>
          </div>
          <div className={showAudit ? 'grid grid-cols-1 xl:grid-cols-2 gap-4' : ''}>
            {/* LEFT: existing entry form + list of appraisal defects */}
            <div>
              {defects.length === 0 && !adding && (
                <p className="text-xs text-slate-400 italic py-2">No defects logged for this area yet.</p>
              )}
              <div className="space-y-2">
                {defects.map(d => (
                  editingId === d.id ? (
                    <DefectForm
                      key={d.id}
                      area={area}
                      scripts={scripts}
                      roofKey={roofKey}
                      initial={d}
                      isEdit
                      onScriptsChanged={onChange}
                      onSaved={() => { setEditingId(null); onChange(); }}
                      onCancel={() => setEditingId(null)}
                      appraisalId={appraisalId}
                      api={api}
                    />
                  ) : (
                    <DefectRow
                      key={d.id}
                      defect={d}
                      areaKey={area.key}
                      appraisalId={appraisalId}
                      onChange={onChange}
                      onEdit={() => setEditingId(d.id)}
                      onDelete={async () => {
                        if (!window.confirm(`Delete this ${area.label.toLowerCase()} defect entry?`)) return;
                        try {
                          await api.delete(`/appraisals/${appraisalId}/defects/${area.key}/${d.id}`);
                          onChange();
                        } catch (e) {
                          alert('Delete failed: ' + (e?.response?.data?.detail || e.message));
                        }
                      }}
                    />
                  )
                ))}
                {adding && (
                  <DefectForm
                    area={area}
                    scripts={scripts}
                    roofKey={roofKey}
                    onScriptsChanged={onChange}
                    onSaved={() => { setAdding(false); onChange(); }}
                    onCancel={() => setAdding(false)}
                    appraisalId={appraisalId}
                    api={api}
                  />
                )}
                {!adding && (
                  <button
                    type="button"
                    data-testid={`defect-area-add-${area.key}`}
                    onClick={() => setAdding(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[#316536] hover:bg-emerald-50 rounded-md border border-dashed border-[#316536]/40 transition-colors"
                  >
                    <Plus size={12} /> Add {area.label.toLowerCase()} defect
                  </button>
                )}
              </div>
            </div>
            {/* RIGHT: mini audit studio for the current area's library */}
            {showAudit && (
              <MultiDefectAuditPanel
                area={area}
                api={api}
                currentRoofKey={roofKey}
                onScriptsChanged={onChange}
                LibraryEditModal={LibraryEditModal}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DefectRow — one line in the defect table.
//
// iter298p — Photo slot is now click-to-upload AND drag-and-drop enabled.
// Prior to this the 16×16 photo box was decorative-only and users had to
// re-create the defect to attach a photo. Now: click empties open the
// OS file picker, drop-target overlay activates on dragover, and a
// dropped/picked file POSTs to the new photo-swap endpoint which
// overwrites the file on disk in place (photo_url stays stable).
// ---------------------------------------------------------------------------
function DefectRow({ defect, areaKey, appraisalId, onEdit, onDelete, onChange }) {
  const { api } = useAuth();
  const [photoSrc, setPhotoSrc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const objectUrlRef = useRef(null);

  // Photos are auth-gated — fetch as blob and turn into an object URL so
  // the <img> can render inline without the auth header.
  useEffect(() => {
    let cancelled = false;
    if (!defect.photo_url) return;
    (async () => {
      try {
        const res = await api.get(defect.photo_url.replace('/api', ''), { responseType: 'blob' });
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        objectUrlRef.current = url;
        setPhotoSrc(url);
      } catch { /* silent — thumbnail just stays hidden */ }
    })();
    return () => {
      cancelled = true;
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, [defect.photo_url, api]);

  const uploadFile = async (file) => {
    if (!file) return;
    // Cheap client-side guardrail — matches backend's ALLOWED_PHOTO_TYPES.
    // Backend re-validates so this is UX polish only (fast error).
    if (!/^image\//i.test(file.type)) {
      setUploadError('Please choose an image file (jpg/png/webp).');
      return;
    }
    setUploadError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      await api.post(
        `/appraisals/${appraisalId}/defects/${areaKey}/${defect.id}/photo`,
        fd,
      );
      onChange?.();  // re-fetch defect list so the new photo_url flows in
    } catch (e) {
      setUploadError(e?.response?.data?.detail || e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    // Q3(a) — dropped file always replaces the existing photo. Take the
    // first image file dropped; ignore non-image drops silently so
    // accidental text/link drops don't error out.
    const files = Array.from(e.dataTransfer?.files || []);
    const img = files.find(f => /^image\//i.test(f.type));
    if (img) uploadFile(img);
  };

  return (
    <div
      data-testid={`defect-row-${areaKey}-${defect.id}`}
      className="flex items-start gap-3 p-3 rounded-md border border-[#E2E2DF] bg-white"
    >
      <label
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        data-testid={`defect-photo-uploader-${defect.id}`}
        title="Click to choose a photo, or drag & drop"
        className={`relative w-16 h-16 shrink-0 rounded border ${dragOver ? 'border-[#316536] border-dashed bg-emerald-50' : 'border-[#E2E2DF]'} cursor-pointer overflow-hidden transition-colors`}
      >
        <input
          type="file"
          accept="image/*"
          className="hidden"
          disabled={uploading}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ''; }}
        />
        {photoSrc ? (
          <img src={photoSrc} alt={defect.defect_type || 'defect'} className="w-16 h-16 object-cover" />
        ) : defect.photo_url ? (
          <div className="w-16 h-16 bg-slate-100 animate-pulse" />
        ) : (
          <div className="w-16 h-16 bg-slate-50 flex items-center justify-center text-slate-300 border border-dashed border-[#E2E2DF] rounded">
            <ImageIcon size={16} />
          </div>
        )}
        {(uploading || dragOver) && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70 text-[9px] font-bold uppercase tracking-wider text-[#316536]">
            {uploading ? 'Uploading…' : 'Drop'}
          </div>
        )}
      </label>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-slate-900">{defect.defect_type || <em className="text-slate-400">(untyped)</em>}</span>
          <span className="text-[10px] font-mono px-1.5 py-0 rounded bg-slate-100 text-slate-600">qty {defect.qty}</span>
        </div>
        {defect.comment && <p className="text-xs text-slate-700 mt-1 leading-snug whitespace-pre-wrap">{defect.comment}</p>}
        {defect.recommendation && (
          <p className="text-xs text-[#316536] mt-1 leading-snug whitespace-pre-wrap">
            <span className="font-semibold uppercase text-[9px] tracking-wider">Rec:</span> {defect.recommendation}
          </p>
        )}
        {uploadError && <p className="text-[10px] text-red-600 mt-1" data-testid={`defect-photo-error-${defect.id}`}>{uploadError}</p>}
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        <button
          onClick={onEdit}
          data-testid={`defect-edit-${defect.id}`}
          title="Edit"
          className="p-1.5 text-slate-400 hover:text-[#D35400]"
        >
          <Edit3 size={12} />
        </button>
        <button
          onClick={onDelete}
          data-testid={`defect-delete-${defect.id}`}
          title="Delete"
          className="p-1.5 text-slate-400 hover:text-red-500"
        >
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DefectForm — inline add/edit for a per-appraisal defect entry.
//
// iter298f — reads its Defect Type dropdown from the standalone
// `multi_defect_scripts` collection (filtered by roof_key at load
// time in the parent). On dropdown selection, Comment + Recommendation
// auto-fill from the picked script — but the operator can still hand-
// edit those fields (per-appraisal only; source script stays untouched
// unless they use the ✎ / + / 📄 buttons to persist library edits).
//
// Three new buttons next to the dropdown:
//   + Add        → create a new script for THIS area × current roof_key
//   ✎ Edit       → save name/comment/recommendation changes back to the
//                  source multi_defect_scripts row
//   📄 Copy from → modal picker of scripts from OTHER roof_types (same
//                  area) — click to POST /copy for the current roof_key
// ---------------------------------------------------------------------------
function DefectForm({ area, scripts, roofKey, initial, isEdit, onSaved, onCancel, onScriptsChanged, appraisalId, api }) {
  const initialTypeId = initial?._script_id || (scripts.find(s => s.name === initial?.defect_type)?.id) || '';
  const [selectedScriptId, setSelectedScriptId] = useState(initialTypeId);
  const [defect_type, setDefectType] = useState(initial?.defect_type || '');
  const [qty, setQty] = useState(initial?.qty || 1);
  const [comment, setComment] = useState(initial?.comment || '');
  const [recommendation, setRecommendation] = useState(initial?.recommendation || '');
  const [photoFile, setPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // iter298f · CRUD sub-modes for the 3 buttons next to the dropdown.
  const [subMode, setSubMode] = useState(null); // null | 'add' | 'edit' | 'copy'
  const [libraryBusy, setLibraryBusy] = useState(false);

  // Auto-fill Comment + Recommendation from the picked script — always
  // when the operator switches type (matches user's "select from list"
  // expectation of the redesign). Empty string comment/recommendation
  // on the source is respected (leaves the field blank rather than
  // wiping user input).
  const onDefectTypeChange = (nextId) => {
    setSelectedScriptId(nextId);
    if (!nextId) { setDefectType(''); return; }
    const s = scripts.find(x => x.id === nextId);
    if (!s) return;
    setDefectType(s.name);
    if (s.comment) setComment(s.comment);
    if (s.recommendation) setRecommendation(s.recommendation);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      if (isEdit) {
        await api.patch(
          `/appraisals/${appraisalId}/defects/${area.key}/${initial.id}`,
          { defect_type, qty, comment, recommendation },
        );
      } else {
        const fd = new FormData();
        fd.append('defect_type', defect_type);
        fd.append('qty', String(qty));
        fd.append('comment', comment);
        fd.append('recommendation', recommendation);
        if (photoFile) fd.append('photo', photoFile);
        await api.post(`/appraisals/${appraisalId}/defects/${area.key}`, fd);
      }
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Library CRUD helpers ────────────────────────────────────────────────
  const saveLibraryEdit = async (payload) => {
    if (!selectedScriptId) return;
    setLibraryBusy(true);
    try {
      await api.put(`/multi-defect-scripts/${selectedScriptId}`, payload);
      onScriptsChanged?.();  // reloads the parent's scripts prop
      setSubMode(null);
    } catch (e) {
      alert('Save failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLibraryBusy(false);
    }
  };

  const saveLibraryAdd = async (payload) => {
    setLibraryBusy(true);
    try {
      // iter298h — respect the modal's explicit roof_types selection if
      // the operator ticked any boxes. Falls back to the appraisal's
      // roofKey (single tag) when the modal returned no explicit roof_types,
      // preserving the pre-iter298h "auto-tag by current roof" behaviour.
      const explicitRoofTypes = Array.isArray(payload.roof_types) ? payload.roof_types : null;
      const roof_types = explicitRoofTypes !== null
        ? explicitRoofTypes
        : (roofKey ? [roofKey] : []);
      const r = await api.post('/multi-defect-scripts', {
        area:  area.key,
        name:  payload.name,
        comment: payload.comment,
        recommendation: payload.recommendation,
        roof_types,
      });
      // Auto-select the newly created entry.
      const newId = r.data?.id;
      onScriptsChanged?.();
      if (newId) {
        setSelectedScriptId(newId);
        setDefectType(payload.name);
        setComment(payload.comment || '');
        setRecommendation(payload.recommendation || '');
      }
      setSubMode(null);
    } catch (e) {
      alert('Add failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLibraryBusy(false);
    }
  };

  const saveLibraryCopy = async (srcId) => {
    if (!roofKey) {
      alert('Copy-from requires the appraisal to have a roof_key set.');
      return;
    }
    setLibraryBusy(true);
    try {
      const r = await api.post(`/multi-defect-scripts/${srcId}/copy`, { target_roof_key: roofKey });
      const newId = r.data?.id;
      onScriptsChanged?.();
      if (newId) {
        setSelectedScriptId(newId);
        setDefectType(r.data.name);
        setComment(r.data.comment || '');
        setRecommendation(r.data.recommendation || '');
      }
      setSubMode(null);
    } catch (e) {
      alert('Copy failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLibraryBusy(false);
    }
  };

  return (
    <div
      data-testid={`defect-form-${area.key}`}
      className="p-3 rounded-md border border-[#316536] bg-emerald-50/40 space-y-2"
    >
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_100px] gap-2">
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Defect Type</label>
          <div className="flex gap-1.5">
            <select
              data-testid={`defect-form-type-${area.key}`}
              value={selectedScriptId}
              onChange={e => onDefectTypeChange(e.target.value)}
              className="flex-1 min-w-0 px-2 py-1.5 border border-[#E2E2DF] rounded-md text-xs bg-white"
            >
              <option value="">— select —</option>
              {scripts.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
              {defect_type && !scripts.some(s => s.name === defect_type) && (
                <option value="">{defect_type} (custom)</option>
              )}
            </select>
            {/* iter298f · Library CRUD buttons — Add / Edit / Copy from. */}
            <button
              type="button"
              onClick={() => setSubMode('add')}
              className="flex items-center gap-0.5 px-1.5 py-1 text-[10px] font-semibold text-[#316536] border border-[#316536] rounded hover:bg-[#316536] hover:text-white transition-colors"
              title="Add a new defect type to the library for this area × roof-type"
              data-testid={`defect-lib-add-${area.key}`}
            >
              <Plus size={10} /> Add
            </button>
            <button
              type="button"
              onClick={() => setSubMode('edit')}
              disabled={!selectedScriptId}
              className="flex items-center gap-0.5 px-1.5 py-1 text-[10px] font-semibold text-slate-600 border border-slate-400 rounded hover:bg-slate-600 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Edit the currently-selected defect type in the library (name / comment / recommendation)"
              data-testid={`defect-lib-edit-${area.key}`}
            >
              <Edit3 size={10} /> Edit
            </button>
            <button
              type="button"
              onClick={() => setSubMode('copy')}
              className="flex items-center gap-0.5 px-1.5 py-1 text-[10px] font-semibold text-purple-700 border border-purple-500 rounded hover:bg-purple-600 hover:text-white transition-colors"
              title="Copy a defect type from another roof-type into this one"
              data-testid={`defect-lib-copy-${area.key}`}
            >
              📄 Copy from
            </button>
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Qty</label>
          <input
            type="number"
            min={1}
            data-testid={`defect-form-qty-${area.key}`}
            value={qty}
            onChange={e => setQty(Math.max(1, parseInt(e.target.value, 10) || 1))}
            className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded-md text-xs bg-white"
          />
        </div>
      </div>
      <div>
        <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Comment</label>
        <textarea
          rows={4}
          data-testid={`defect-form-comment-${area.key}`}
          value={comment}
          onChange={e => setComment(e.target.value)}
          className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded-md text-xs bg-white leading-relaxed"
        />
      </div>
      <div>
        <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Recommendation</label>
        <textarea
          rows={3}
          data-testid={`defect-form-recommendation-${area.key}`}
          value={recommendation}
          onChange={e => setRecommendation(e.target.value)}
          placeholder="One recommendation per line, or a short paragraph. e.g. Replace 3 tiles with matched colour"
          className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded-md text-xs bg-white leading-relaxed"
        />
      </div>
      {!isEdit && (
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Photo (optional)</label>
          <label className="flex items-center gap-2 px-2 py-1.5 border border-dashed border-[#E2E2DF] rounded-md text-xs bg-white cursor-pointer hover:border-slate-300">
            <Camera size={12} className="text-slate-400" />
            <input
              type="file"
              accept="image/*"
              data-testid={`defect-form-photo-${area.key}`}
              onChange={e => setPhotoFile(e.target.files?.[0] || null)}
              className="hidden"
            />
            {photoFile ? (
              <span className="text-slate-700 truncate">{photoFile.name}</span>
            ) : (
              <span className="text-slate-400">Choose file…</span>
            )}
            {photoFile && (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); setPhotoFile(null); }}
                className="text-slate-400 hover:text-red-500"
              >
                <X size={12} />
              </button>
            )}
          </label>
        </div>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving || !defect_type}
          data-testid={`defect-form-save-${area.key}`}
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-[#D35400] hover:bg-[#A04000] text-white rounded-md disabled:opacity-50"
        >
          <Save size={12} /> {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add defect'}
        </button>
      </div>

      {/* iter298f · Library CRUD sub-modals */}
      {subMode === 'add' && (
        <LibraryEditModal
          title={`Add ${area.label} defect type to library`}
          initial={{ name: '', comment: '', recommendation: '', roof_types: roofKey ? [roofKey] : [] }}
          busy={libraryBusy}
          onSave={saveLibraryAdd}
          onCancel={() => setSubMode(null)}
        />
      )}
      {subMode === 'edit' && (() => {
        const s = scripts.find(x => x.id === selectedScriptId);
        if (!s) return null;
        return (
          <LibraryEditModal
            title={`Edit ${area.label} defect type in library`}
            initial={{ name: s.name, comment: s.comment, recommendation: s.recommendation, roof_types: s.roof_types || [] }}
            busy={libraryBusy}
            onSave={saveLibraryEdit}
            onCancel={() => setSubMode(null)}
          />
        );
      })()}
      {subMode === 'copy' && (
        <LibraryCopyModal
          area={area}
          currentRoofKey={roofKey}
          api={api}
          busy={libraryBusy}
          onCopy={saveLibraryCopy}
          onCancel={() => setSubMode(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// LibraryEditModal — small centred modal for library Add / Edit.
// Shared between the ✎ Edit and + Add buttons since both need the same
// 3 inputs (name, comment, recommendation). Add resets to empty
// values, Edit pre-populates from the currently-selected script.
// ---------------------------------------------------------------------------
export function LibraryEditModal({ title, initial, busy, onSave, onCancel }) {
  const [name, setName] = useState(initial.name || '');
  const [comment, setComment] = useState(initial.comment || '');
  const [recommendation, setRecommendation] = useState(initial.recommendation || '');
  // iter298h — roof_types checklist. Empty array = "generic" (applies to
  // all roofs). Ticking any concrete type flips OFF the generic mode.
  // Uses the same 6 canonical roof_keys the multi_defect_scripts import
  // canonicalises to — matches what appears in the picker filter.
  const [roofTypes, setRoofTypes] = useState(Array.isArray(initial.roof_types) ? initial.roof_types : []);
  const ROOF_KEYS = [
    { key: 'longrun_iron',    label: 'LongRun Iron' },
    { key: 'modern_concrete', label: 'Modern Concrete' },
    { key: 'old_concrete',    label: 'Old Concrete' },
    { key: 'clay_tile',       label: 'Clay Tile' },
    { key: 'color_tile',      label: 'ColorTile' },
    { key: 'decramastic',     label: 'Decramastic' },
  ];
  const isGeneric = roofTypes.length === 0;
  const toggleKey = (k) => {
    setRoofTypes(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4" data-testid="defect-library-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-slate-900">{title}</h4>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="space-y-2.5">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              data-testid="defect-library-name"
              className="w-full px-2.5 py-1.5 border border-[#E2E2DF] rounded text-xs bg-white"
              placeholder="e.g. Heavily Cracked"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Comment</label>
            <textarea
              rows={5}
              value={comment}
              onChange={e => setComment(e.target.value)}
              data-testid="defect-library-comment"
              className="w-full px-2.5 py-1.5 border border-[#E2E2DF] rounded text-xs bg-white leading-relaxed"
              placeholder="Full paragraph that auto-fills when this defect is selected."
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Recommendation</label>
            <textarea
              rows={3}
              value={recommendation}
              onChange={e => setRecommendation(e.target.value)}
              data-testid="defect-library-recommendation"
              className="w-full px-2.5 py-1.5 border border-[#E2E2DF] rounded text-xs bg-white leading-relaxed"
              placeholder="Short recommendation. e.g. Bed and Point"
            />
          </div>
          <div data-testid="defect-library-roof-types">
            <label className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              <span>Roof types</span>
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono ${isGeneric ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                {isGeneric ? 'GENERIC · all roofs' : `${roofTypes.length} selected`}
              </span>
            </label>
            <div className="grid grid-cols-2 gap-1.5 p-2 border border-[#E2E2DF] rounded bg-slate-50/60">
              {ROOF_KEYS.map(rk => (
                <label
                  key={rk.key}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer transition-colors text-xs ${roofTypes.includes(rk.key) ? 'bg-purple-100 border border-purple-300' : 'bg-white border border-slate-200 hover:border-purple-300'}`}
                  data-testid={`defect-library-roof-${rk.key}`}
                >
                  <input
                    type="checkbox"
                    checked={roofTypes.includes(rk.key)}
                    onChange={() => toggleKey(rk.key)}
                    className="accent-purple-600"
                  />
                  <span className="font-semibold text-slate-800">{rk.label}</span>
                </label>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <button
                type="button"
                onClick={() => setRoofTypes([])}
                className="text-[9px] font-semibold text-blue-700 hover:underline"
                data-testid="defect-library-roof-set-generic"
              >
                🌐 Set as Generic (all roofs)
              </button>
              <span className="text-slate-300">·</span>
              <button
                type="button"
                onClick={() => setRoofTypes(['modern_concrete', 'old_concrete', 'clay_tile'])}
                className="text-[9px] font-semibold text-purple-700 hover:underline"
              >
                🏠 Tile family
              </button>
              <button
                type="button"
                onClick={() => setRoofTypes(['color_tile', 'decramastic'])}
                className="text-[9px] font-semibold text-purple-700 hover:underline"
              >
                🔩 Metal tile family
              </button>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-4">
          <button onClick={onCancel} className="px-3 py-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700">Cancel</button>
          <button
            onClick={() => onSave({ name, comment, recommendation, roof_types: roofTypes })}
            disabled={busy || !name.trim()}
            data-testid="defect-library-save"
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-[#316536] hover:bg-[#254D28] text-white rounded-md disabled:opacity-50"
          >
            <Save size={12} /> {busy ? 'Saving…' : 'Save to library'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LibraryCopyModal — picker showing scripts from OTHER roof_types for the
// same area. Fetches the full GET /multi-defect-scripts?area=… list and
// filters out entries that already match the current roof_key.
// ---------------------------------------------------------------------------
function LibraryCopyModal({ area, currentRoofKey, api, busy, onCopy, onCancel }) {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/multi-defect-scripts?area=${encodeURIComponent(area.key)}`);
        if (cancelled) return;
        // Exclude entries already covering the current roof_key.
        const list = (r.data.scripts || []).filter(s => {
          if (!currentRoofKey) return true;
          return !((s.roof_types || []).includes(currentRoofKey));
        });
        setAll(list);
      } catch (e) {
        alert('Load failed: ' + (e?.response?.data?.detail || e.message));
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, area.key, currentRoofKey]);

  const filtered = all.filter(s => {
    if (!filterText) return true;
    const t = filterText.toLowerCase();
    return s.name.toLowerCase().includes(t) || (s.comment || '').toLowerCase().includes(t);
  });

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4" data-testid="defect-copy-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl p-5 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-bold text-slate-900">
            Copy {area.label} defect from another roof-type
            {currentRoofKey && <span className="ml-2 text-[10px] font-mono text-slate-500">→ {currentRoofKey}</span>}
          </h4>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <p className="text-[11px] text-slate-500 mb-3">
          Click any entry to create an independent copy tagged with the current roof-type. Editing the copy will NOT affect the original.
        </p>
        <input
          type="text"
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
          placeholder="Filter by name or comment…"
          className="w-full px-2.5 py-1.5 border border-[#E2E2DF] rounded text-xs bg-white mb-2"
          data-testid="defect-copy-filter"
        />
        <div className="flex-1 overflow-y-auto border border-[#E2E2DF] rounded" data-testid="defect-copy-list">
          {loading ? (
            <div className="p-4 text-xs text-slate-400"><Loader2 size={12} className="inline animate-spin mr-1.5" /> Loading library…</div>
          ) : filtered.length === 0 ? (
            <p className="p-4 text-xs text-slate-400 italic">No matching entries in the library.</p>
          ) : (
            filtered.map(s => (
              <button
                key={s.id}
                type="button"
                onClick={() => onCopy(s.id)}
                disabled={busy}
                data-testid={`defect-copy-item-${s.id}`}
                className="w-full text-left px-3 py-2 border-b border-[#E2E2DF] hover:bg-purple-50 transition-colors disabled:opacity-50"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-800">{s.name}</span>
                  <span className="text-[9px] font-mono text-slate-400">{(s.roof_types || []).join(', ') || 'generic'}</span>
                </div>
                {s.comment && <p className="text-[10px] text-slate-500 mt-0.5 line-clamp-2">{s.comment}</p>}
              </button>
            ))
          )}
        </div>
        <div className="flex items-center justify-end gap-2 mt-3">
          <button onClick={onCancel} className="px-3 py-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700">Cancel</button>
        </div>
      </div>
    </div>
  );
}
